/**
 * Custom React hook to retrieve selection information associated to the NEW_COMMENT_ID
 * from the current content.
 *
 * @returns A function that returns an object containing either `nodeSelection` or
 * `markSelection` information based on the current selection in the editor.
 */

import { useContext } from 'react';

import findNodeWithSelection from '../helpers/findNodeWithSelection.helper';
import getNodeHtml from '@modules/documents/helpers/getNodeHtml.helper';
import { NEW_COMMENT_ID, SELECTION_NODE_NAME } from '../constants';

import { DocumentContext, getCommentFieldSelection, isBoundDocument } from '@modules/documents';
import { TFieldSelection, TMarkSelection, TNodeSelection } from '@modules/comments';

const useGetSelectionInfo = () => {
  const documentContext = useContext(DocumentContext);

  return (): {
    nodeSelection?: TNodeSelection;
    markSelection?: TMarkSelection;
    fieldSelection?: TFieldSelection;
  } => {
    if (!documentContext) return {};

    const { editor } = documentContext;
    const { schema } = editor;

    // Bound documents anchor the selection inside a field value, not the body blob.
    if (isBoundDocument(editor.state)) {
      const fieldSelection = getCommentFieldSelection(editor, NEW_COMMENT_ID);
      return fieldSelection ? { fieldSelection } : {};
    }

    const [node] = findNodeWithSelection(editor);

    if (node && node.type.name === SELECTION_NODE_NAME) {
      const nodeIds: string[] = [];
      node.forEach((child) => {
        // if the top-level children have an `id` in their attrs:
        if (child.attrs?.id) nodeIds.push(child.attrs.id);
      });
      return { nodeSelection: { nodeIds } };
    }

    if (node && node.type.name === 'paragraph') {
      return {
        markSelection: {
          paragraphId: node.attrs.id,
          paragraphHtml: getNodeHtml(node, schema),
        },
      };

      /*
      let startIdx = -1;
      let endIdx = Infinity;
      let paragraphHtml = '';

      node.descendants(child => {
        const hasSelectionMark = child.marks.some(m => m.type.name === 'selection-mark');
        if (hasSelectionMark) {
          if (startIdx < 0)
            startIdx = paragraphHtml.length;

        } else if (startIdx >= 0 && endIdx === Infinity)
          endIdx = paragraphHtml.length;
  
        if (child.isText)
          paragraphHtml += child.text ?? ''
        else if (child.type.name === 'reference')
          paragraphHtml += '\\';      
      });
      endIdx = Math.min(paragraphHtml.length, endIdx);

      if (startIdx >= 0) {
        const selectedText = paragraphHtml.substring(startIdx, endIdx);
        // const matchIdx = paragraphHtml.substring(0, startIdx).split(selectedText).length;
        return { markSelection: {
          paragraphId: node.attrs.id,
          selectedText,
          start: startIdx,
          end: endIdx
        } };
      }
      */
    }

    return {};
  };
};

export default useGetSelectionInfo;
